// content-synchronization.service.ts
import { Injectable } from '@angular/core';
import { concat, Observable, of, throwError } from 'rxjs';
import { catchError, tap, toArray } from 'rxjs/operators';
import { StructureContent } from '../pages/models/content.model';
import { ContentApiService } from './content-api.service';
import { ContentStorageService } from './content-storage.service';

/**
 * Serwis odpowiedzialny za synchronizację zmian między lokalnymi strukturami a backendem
 * Zarządza wersjonowaniem danych i obsługuje konflikty zmian
 */
@Injectable({
  providedIn: 'root',
})
export class ContentSynchronizationService {
  private readonly BLOCK_PREFIX = 'block_';
  private readonly MATRIX_PREFIX = 'matrix_';

  constructor(
    private storageService: ContentStorageService,
    private apiService: ContentApiService,
  ) {
  }

  /**
   * Synchronizuje wszystkie zmodyfikowane struktury z serwerem
   * @returns Observable z tablicą wyników synchronizacji
   * @remarks Wykonuje szeregowe żądania PUT dla każdej zmienionej struktury
   */
  synchronizeChangedStructures(): Observable<any[]> {
    const changedStructures = this.findChangedStructures();

    console.log('[ContentSynchronizationService->synchronizeChangedStructures] changedStructures', changedStructures);
    if (changedStructures.length === 0) {
      return of([]);
    }

    const clientId = this.storageService.findClientIdInStored();
    console.log('[ContentSynchronizationService->synchronizeChangedStructures] clientId', clientId);
    if (!clientId) {
      return throwError(() => new Error('Brak client_id'));
    }

    const results: Observable<any>[] = [];

    changedStructures.forEach(structure => {
      console.log('[ContentSynchronizationService->changedStructure] structure', structure);
      const request$ = this.apiService.sendStructureToApi(structure, clientId).pipe(
        tap(() => {
          this.handleSuccessfulSave(structure.id);
        }),
        catchError(error => {
          console.error('Błąd synchronizacji:', error);
          return throwError(() => error);
        }),
      );

      results.push(request$);
    });

    return concat(...results).pipe(
      toArray(),
    );
  }

  /**
   * Wyszukuje struktury z niezsynchronizowanymi zmianami
   * @returns Lista zmodyfikowanych struktur
   * @remarks Porównuje wersję 'working' z 'original' dla każdej struktury
   */
  private findChangedStructures(): StructureContent[] {
    const changedStructures: StructureContent[] = [];
    const allBlockKeys = this.storageService.getAllBlockStructureKeys();

    allBlockKeys.forEach(key => {
      if (key.endsWith('_working')) {
        const baseKey = key.replace('_working', '');
        const originalStructure = this.storageService.getContent<StructureContent>(`${baseKey}_original`);
        const workingStructure = this.storageService.getContent<StructureContent>(key);

        if (originalStructure && workingStructure &&
          !this.areStructuresEqual(originalStructure, workingStructure)) {
          changedStructures.push(workingStructure);
        }
      }
    });

    return changedStructures;
  }

  /**
   * Obsługuje sukces synchronizacji - czyści historię zmian
   * @param structureId - ID pomyślnie zsynchroniczonej struktury
   * @remarks Usuwa obie wersje (original/working) i przeładowuje stronę
   */
  private handleSuccessfulSave(structureId: string): void {
    const baseKey = `${this.BLOCK_PREFIX}${structureId}`;
    const matrixKey = `${this.MATRIX_PREFIX}${structureId}`;

    this.storageService.removeItems([
      `${baseKey}_original`,
      `${baseKey}_working`,
      `${matrixKey}_original`,
      `${matrixKey}_working`,
    ]);
  }

  /**
   * Oznacza strukturę jako zsynchronizowaną bez wysyłania do API
   * @param structureId - ID struktury do oznaczenia
   * @remarks Nadpisuje wersję original aktualną wersją working
   */
  markAsSynced(structureId: string): void {
    const workingKey = `block_${structureId}_working`;
    const originalKey = `block_${structureId}_original`;
    const workingStructure = localStorage.getItem(workingKey);

    if (workingStructure) {
      localStorage.setItem(originalKey, workingStructure);
    }
  }

  /**
   * Porównuje dwie struktury pod kątem identyczności
   * @returns true jeśli struktury są identyczne
   * @remarks Wykonuje głębokie porównanie przez stringify
   */
  private areStructuresEqual(a: StructureContent, b: StructureContent): boolean {
    try {
      return JSON.stringify(a.structure) === JSON.stringify(b.structure);
    } catch (e) {
      return false;
    }
  }

  /**
   * Przywraca wszystkie struktury do ostatniej zsynchronizowanej wersji
   * @remarks Czyści wszystkie lokalne zmiany i przeładowuje aplikację
   */
  discardAllBlockChangesAndReload(): void {
    const keys = Object.keys(localStorage)
      .filter(k => k.startsWith(this.BLOCK_PREFIX) && k.endsWith('_original'));

    keys.forEach(originalKey => {
      const baseKey = originalKey.replace('_original', '');
      const workingKey = `${baseKey}_working`;
      const originalValue = localStorage.getItem(originalKey);

      if (originalValue) {
        localStorage.setItem(workingKey, originalValue);
      }
    });

    window.location.reload();
  }

  /**
   * Odrzuca zmiany dla pojedynczej struktury
   * @param structureId - ID struktury do przywrócenia
   * @remarks Nadpisuje wersję working wersją original dla konkretnej struktury
   */
  discardBlockChanges(structureId: string): void {
    const originalKey = `${this.BLOCK_PREFIX}${structureId}_original`;
    const workingKey = `${this.BLOCK_PREFIX}${structureId}_working`;
    const originalValue = localStorage.getItem(originalKey);

    if (originalValue) {
      localStorage.setItem(workingKey, originalValue);
    }
  }

  /**
   * Sprawdza czy istnieją niezapisane zmiany w strukturach
   * @returns true jeśli znaleziono jakiekolwiek różnice między wersjami working i original
   */
  public hasUnsavedChanges(): boolean {
    return this.findChangedStructures().length > 0;
  }
}
